import React, { useRef } from 'react'
import {
  Controller,
  Control,
  ControllerRenderProps,
  FieldValues,
  Path,
  PathValue
} from 'react-hook-form'
import classes from './styles.module.scss'

const NON_CODE_CHARS = /[^A-Za-z0-9]/g

export const normalizePinCode = (chars: string, maxLength: number) =>
  chars.replace(NON_CODE_CHARS, '').toUpperCase().slice(0, maxLength)

interface PinInputProps<TFieldValues extends FieldValues = FieldValues> {
  label?: string
  pinLength: number
  control: Control<TFieldValues>
  name: Path<TFieldValues>
}

export const PinInput = <TFieldValues extends FieldValues>(
  props: PinInputProps<TFieldValues>
) => {
  const { pinLength, control, name, label } = props
  const inputRefs = useRef<HTMLInputElement[]>([])

  type PinField = ControllerRenderProps<TFieldValues, Path<TFieldValues>>

  const applyChars = (field: PinField, chars: string, requestedStart: number) => {
    // A whole code is a replacement wherever it lands (autofill can target any box);
    // anchored at that box it would be truncated into a wrong, incomplete code.
    const isWholeCode = normalizePinCode(chars, pinLength).length === pinLength
    const startIndex = isWholeCode ? 0 : requestedStart
    const normalized = normalizePinCode(chars, pinLength - startIndex)
    if (!normalized) return

    const currentValues = String(field.value ?? '').split('')
    normalized.split('').forEach((char, offset) => {
      currentValues[startIndex + offset] = char
    })
    field.onChange(currentValues.join(''))
    inputRefs.current[Math.min(startIndex + normalized.length, pinLength - 1)]?.focus()
  }

  // Runs on every change, IME composition included: skipping an update makes React
  // restore the controlled value, which cancels the composition and drops the char.
  const writeBoxValue = (field: PinField, value: string, index: number) => {
    const currentValues = String(field.value ?? '').split('')

    if (!value) {
      currentValues[index] = ''
      field.onChange(currentValues.join(''))
      return
    }

    // Typing over a filled box leaves both chars in it (maxLength is bypassed on
    // some mobile keyboards); keep the new one, not the one already shown.
    const previous = currentValues[index]
    const isTypeOver = !!previous && value.length === 2 && value.includes(previous)
    applyChars(field, isTypeOver ? value.replace(previous, '') : value, index)
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, field: PinField) => {
    e.preventDefault()
    applyChars(field, e.clipboardData.getData('Text'), 0)
  }

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    field: PinField,
    index: number
  ) => {
    if (e.key === 'Backspace' && !e.currentTarget.value && index > 0) {
      const currentValues = String(field.value ?? '').split('')
      currentValues[index] = ''
      field.onChange(currentValues.join(''))
      inputRefs.current[index - 1]?.focus()
    }
  }

  return (
    <div className={classes.container}>
      {label && <label htmlFor="pairCode">{label}</label>}
      <Controller
        control={control}
        name={name}
        defaultValue={'' as PathValue<TFieldValues, Path<TFieldValues>>}
        render={({ field }) => (
          <div className={classes.pinContainer}>
            {Array.from({ length: pinLength }).map((_, index) => (
              <input
                key={index}
                type="text"
                maxLength={1}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="one-time-code"
                className={classes.pin}
                ref={el => {
                  inputRefs.current[index] = el!
                }}
                value={String(field.value ?? '')[index] || ''}
                onChange={(e) => writeBoxValue(field, e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, field, index)}
                onPaste={(e) => handlePaste(e, field)}
              />
            ))}
          </div>
        )}
      />
    </div>
  )
}
